<template>
  <Modal
    v-model:visible="isVisible"
    class="document-upload-modal"
    :header="t('common.addParam', { param: t('documents.documents') })"
    @keypress.esc="isVisible = false"
  >
    <DocumentUploadPicker @remove="onRemove" @select="onBeforeUpload" />
    <InputError
      v-for="error of v$.file.$errors"
      :id="error.$uid"
      :key="error.$uid"
      :error="error"
    />

    <div
      v-if="isSingleTypeMode || (stagedFile && stagedFile.size > 0)"
      class="document-metadata"
    >
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="document-types"
        class="field"
        :label="
          t('common.paramTypes', {
            param: t('documents.documents'),
            count: 2,
          })
        "
        :errors="v$.properties.documentTypes.$errors"
      >
        <DocumentTypeMultiSelect
          :id="inputId"
          v-model="documentProperties.documentTypes"
          v-bind="ariaProps"
          editable
          :class="inputClasses"
          :invalid="invalid"
          :placeholder="
            t('common.paramTypes', {
              param: t('documents.documents'),
              count: 2,
            })
          "
          :disabled="isSingleTypeMode"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="document-name"
        class="field"
        :label="t('common.paramName', { param: t('documents.documents') })"
        :errors="v$.properties.documentName.$errors"
      >
        <InputText
          :id="inputId"
          v-model="documentProperties.documentName"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :placeholder="
            t('common.paramName', { param: t('documents.documents') })
          "
        />
      </InputWrapper>
    </div>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-upload"
        :label="t('upload.upload')"
        @click="onUploadDocument"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { CorsairDocumentType } from '@rygen/rygen-ui/model';

import { DocumentProperties, DocumentUploadPayload } from '@/model';

import DocumentTypeMultiSelect from '@/components/document/DocumentTypeMultiSelect.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import DocumentUploadPicker from '@rygen/rygen-ui/components/document/DocumentUploadPicker.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  docType: {
    type: String as PropType<CorsairDocumentType>,
    default: () => undefined,
  },
  documentTypesRequired: {
    type: Boolean,
    default: () => false,
  },
});
const { docType, documentTypesRequired } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'upload', payload: DocumentUploadPayload): void;
}>();

const { t } = useI18n();
const { uid } = useUid('document-upload-modal');

const stagedFile: Ref<File | undefined> = ref();
const documentProperties = ref(new DocumentProperties());
const combinedModel = toRef(() => ({
  file: stagedFile.value,
  properties: documentProperties.value,
}));

type MaybeNestedValidationRule =
  | Record<string, ValidationRule>
  | Record<string, Record<string, ValidationRule>>;
const rules = computed(() => {
  const r: Record<string, MaybeNestedValidationRule> = {
    file: {
      required: required(t('documents.documents')),
    },
    properties: {
      documentName: {
        required: required(
          t('common.paramName', { param: t('documents.documents') }),
        ),
      },
      documentTypes: {},
    },
  };

  if (documentTypesRequired.value) {
    r.properties.documentTypes = {
      ...r.properties.documentTypes,
      required: required(
        t('common.paramTypes', {
          param: t('documents.documents'),
          count: 2,
        }),
      ),
    };
  }

  return r;
});

const { v$, validate } = useValidations(combinedModel, rules, uid);

const isSingleTypeMode = computed(() => !!docType?.value);

const onBeforeUpload = (file: File) => {
  stagedFile.value = file;
  documentProperties.value.documentName = file.name;
  v$.value.$reset();
};

const onUploadDocument = async () => {
  // `validate` ensures `stagedFile`is defined, but typescript doesn't know that
  if (!(await validate()) || !stagedFile.value) {
    return;
  }

  emit('upload', {
    file: stagedFile.value,
    properties: documentProperties.value,
  });
  isVisible.value = false;
};

const onRemove = () => {
  stagedFile.value = undefined;
  documentProperties.value.documentName = '';
  v$.value.$reset();
};

const onCancel = () => {
  isVisible.value = false;
  emit('cancel');
};

watch(isVisible, (newVal: boolean) => {
  v$.value.$reset();

  if (newVal) {
    documentProperties.value.documentTypes = docType?.value
      ? [docType.value]
      : [];
  } else {
    documentProperties.value = new DocumentProperties();
    stagedFile.value = undefined;
  }
});

watch(
  () => docType?.value,
  (newVal: CorsairDocumentType | undefined) => {
    if (newVal) {
      documentProperties.value.documentTypes = [newVal];
    }
  },
);
</script>

<style>
.document-upload-modal.p-dialog {
  .document-metadata {
    width: 100%;
    margin-top: var(--sp-6);
  }
}
</style>
